import { Link } from "@tanstack/react-router";
import { TriangleAlertIcon } from "lucide-react";

import { deriveCloudRuntimeConnectionFailure } from "../cloudRuntimeConnectionStatus";
import type { CloudRuntimeTerminalFailureResidual } from "../cloudRuntimeTerminalFailureResidual";

const FEEDBACK_ACTION_CLASS_NAME =
  "shrink-0 px-1 py-0.5 font-medium text-foreground hover:text-foreground/75 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring";

export interface NewThreadSubmissionFeedbackProps {
  readonly cloudCatalogActive: boolean;
  readonly error: string | null;
  /** The CMMD refusal code behind `error`, when the refusal carried one. */
  readonly errorCode: string | null;
  /** Safe structured fields from the connect refusal (no raw guest error text). */
  readonly failureResidual?: CloudRuntimeTerminalFailureResidual | null;
  readonly localNotice?: string | null;
  /**
   * What the connect flow is doing while a cold cloud runtime provisions
   * ("Ensuring cloud runtime", "Cloud project is still provisioning", ...).
   * FOR-3579: without this, the composer gave no reason for a send that sat
   * disabled for over two minutes.
   */
  readonly sendingPhase?: string | null;
  readonly onRetry: () => void;
}

export function NewThreadSubmissionFeedback(props: NewThreadSubmissionFeedbackProps) {
  if (props.sendingPhase) {
    return (
      <p role="status" className="mt-2 px-1 text-sm text-muted-foreground">
        {props.sendingPhase}
      </p>
    );
  }
  if (props.localNotice) {
    return (
      <p role="status" className="mt-2 px-1 text-sm text-muted-foreground">
        {props.localNotice}
      </p>
    );
  }
  if (props.cloudCatalogActive) {
    return (
      <NewThreadCloudConnectionFailure
        error={props.error}
        errorCode={props.errorCode}
        failureResidual={props.failureResidual ?? null}
        onRetry={props.onRetry}
      />
    );
  }

  if (props.error === null) return null;
  return (
    <p role="alert" className="mt-2 px-1 text-sm text-destructive-foreground">
      {props.error}
    </p>
  );
}

interface NewThreadCloudConnectionFailureProps {
  readonly error: string | null;
  readonly errorCode: string | null;
  readonly failureResidual: CloudRuntimeTerminalFailureResidual | null;
  readonly onRetry: () => void;
}

function NewThreadCloudConnectionFailure(props: NewThreadCloudConnectionFailureProps) {
  const status = deriveCloudRuntimeConnectionFailure({
    error: props.error,
    errorCode: props.errorCode,
    failureResidual: props.failureResidual,
  });
  if (status === null) return null;

  return (
    <div className="mt-2 px-1 text-xs" role="alert">
      <div className="flex min-h-7 items-center gap-2 text-destructive-foreground">
        <TriangleAlertIcon className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1">{status.label}</span>
        {status.action === "open-environments" ? (
          // Recycle lives on the Environment row in Settings, next to the row's
          // status. A Recycle control here would be a dead button for the wedge
          // that motivates this state: the owner in-place recycle fails against
          // a guest that cannot run exec (FOR-36469).
          <Link
            to="/settings/environments"
            className={FEEDBACK_ACTION_CLASS_NAME}
            data-testid="new-thread-open-environments"
          >
            Open Environments
          </Link>
        ) : status.action === "setup-retry" ? (
          <>
            <Link
              to="/setup"
              className={FEEDBACK_ACTION_CLASS_NAME}
              data-testid="new-thread-open-setup"
            >
              Setup
            </Link>
            <button type="button" className={FEEDBACK_ACTION_CLASS_NAME} onClick={props.onRetry}>
              Retry
            </button>
          </>
        ) : (
          <button type="button" className={FEEDBACK_ACTION_CLASS_NAME} onClick={props.onRetry}>
            Retry
          </button>
        )}
      </div>
      <div className="space-y-1 pl-5 text-muted-foreground">
        {status.action === "open-environments" ? (
          <>
            <p data-testid="terminal-runtime-failure-why">{status.why}</p>
            {status.residualLine !== null ? (
              <p
                className="font-mono text-[11px] leading-snug"
                data-testid="terminal-runtime-failure-residual"
              >
                {status.residualLine}
              </p>
            ) : null}
            <p data-testid="terminal-runtime-failure-code">
              {status.message} Code: {status.errorCode}
            </p>
          </>
        ) : (
          <p>{status.message}</p>
        )}
      </div>
    </div>
  );
}
